import { useCallback, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router";
import { useAtom } from "jotai";
import { patientsLastQueryAtom } from "features/Patients/patients.atoms";
import {
  parsePatientTableQuery,
  PatientTableFilter,
  PatientTableState,
  serializePatientTableQuery,
} from "./patientTableUrl";

export const PATIENTS_PATH = "/patients";

/** URL-backed state for the Patients table. Any change other than paging returns to page 1. */
const usePatientTableState = () => {
  const location = useLocation();
  const history = useHistory();
  const [, setLastQuery] = useAtom(patientsLastQueryAtom);

  const state = useMemo(() => parsePatientTableQuery(location.search), [location.search]);
  const query = useMemo(() => serializePatientTableQuery(state), [state]);

  // Lets the sidebar and the patient page's back link return to this exact view.
  useEffect(() => {
    setLastQuery(query);
  }, [query, setLastQuery]);

  const update = useCallback(
    (changes: Partial<PatientTableState>) => {
      const next = { ...state, page: 1, ...changes };
      history.replace({ pathname: PATIENTS_PATH, search: serializePatientTableQuery(next) });
    },
    [history, state]
  );

  return {
    state,
    query,
    setSearch: (search: string) => update({ search }),
    addFilter: (filter: PatientTableFilter) => update({ filters: [...state.filters, filter] }),
    removeFilter: (index: number) => update({ filters: state.filters.filter((_, i) => i !== index) }),
    replaceFilters: (filters: PatientTableFilter[], sort?: string[]) =>
      update({ filters, ...(sort ? { sort } : {}) }),
    clearAll: () => update({ filters: [], search: "" }),
    /** Click: sort by this column (asc → desc → default). Shift-click: add/toggle it as a secondary sort. */
    toggleSort: (key: string, additive = false) => {
      const index = state.sort.findIndex((s) => s.replace(/^-/, "") === key);
      const current = index >= 0 ? state.sort[index] : null;
      let sort: string[];
      if (additive) {
        if (!current) sort = [...state.sort, key];
        else if (!current.startsWith("-")) sort = state.sort.map((s, i) => (i === index ? `-${key}` : s));
        else sort = state.sort.filter((_, i) => i !== index);
      } else if (current === key && state.sort.length === 1) {
        sort = [`-${key}`];
      } else if (current === `-${key}` && state.sort.length === 1) {
        sort = [];
      } else {
        sort = [key];
      }
      update({ sort });
    },
  };
};

export default usePatientTableState;
